import { useState } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import {
  useUser,
  useUpdateUser,
  useUpdateUserQuota,
  useUserActivity,
  useReassignPlan,
  useDeleteUser,
} from '@/hooks/useUsers'
import { useUserCredits, useAdjustCredits } from '@/hooks/useCredits'
import { useBoosts } from '@/hooks/useBoosts'
import { usePlans } from '@/hooks/usePlans'
import { useHasRole } from '@/hooks/useAuth'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate, formatDateTime, formatCurrency, getApiErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Pencil,
  FileText,
  BarChart3,
  Zap,
  RefreshCw,
  Rocket,
  Trash2,
  Copy,
  Check,
} from 'lucide-react'
import { ReassignPlanRequest, User } from '@/types'

export const UserDetailPage = () => {
  const { userId } = useParams({ strict: false }) as { userId: string }
  const navigate = useNavigate()
  const { data: user, isLoading } = useUser(userId)
  const { data: activity } = useUserActivity(userId)
  const updateUser = useUpdateUser()
  const updateQuota = useUpdateUserQuota()
  const reassignPlan = useReassignPlan()
  const deleteUser = useDeleteUser()
  const { data: plans } = usePlans()
  const { data: boosts } = useBoosts()
  const isSuperAdmin = useHasRole(['super_admin'])

  const { data: credits } = useUserCredits(userId)
  const adjustCredits = useAdjustCredits()

  const [editOpen, setEditOpen] = useState(false)
  const [quotaOpen, setQuotaOpen] = useState(false)
  const [creditsOpen, setCreditsOpen] = useState(false)
  const [reassignOpen, setReassignOpen] = useState(false)
  const [boostOpen, setBoostOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', lastname: '', email: '' })
  // Card 95, item 2: whatsapp_number is kept separate from editForm and only
  // sent when `dirty` — every non-empty save re-stamps whatsapp_opt_in_at
  // server-side, so resending an unchanged number on every "Guardar" would
  // silently re-register consent.
  const [whatsappForm, setWhatsappForm] = useState({ number: '', dirty: false })
  const [quotaValue, setQuotaValue] = useState(0)
  const [adjustForm, setAdjustForm] = useState({
    amount: 0,
    reason: '',
    credit_type: 'extra' as 'plan' | 'extra',
  })
  const [reassignForm, setReassignForm] = useState<ReassignPlanRequest>({
    new_plan_id: '',
    balance_action: 'absorb',
    reason: '',
  })
  const [selectedBoostId, setSelectedBoostId] = useState('')
  const [deleteEmail, setDeleteEmail] = useState('')

  const openEdit = () => {
    if (!user) return
    setEditForm({ name: user.name, lastname: user.lastname, email: user.email })
    setWhatsappForm({ number: '', dirty: false })
    setEditOpen(true)
  }

  const openQuota = () => {
    if (!user) return
    setQuotaValue(user.max_docs)
    setQuotaOpen(true)
  }

  const handleEditSave = async () => {
    if (!user) return
    const updates: Partial<User> = { ...editForm }
    if (whatsappForm.dirty) {
      updates.whatsapp_number = whatsappForm.number.trim()
    }
    try {
      await updateUser.mutateAsync({ id: user.id, updates })
      setEditOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar usuario'))
    }
  }

  const handleQuotaSave = async () => {
    if (!user) return
    if (quotaValue < 0) {
      toast.error('La cuota no puede ser negativa')
      return
    }
    try {
      await updateQuota.mutateAsync({ id: user.id, maxDocs: quotaValue })
      setQuotaOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar cuota'))
    }
  }

  const handleAdjustCredits = async () => {
    if (!user || !adjustForm.reason) return
    try {
      await adjustCredits.mutateAsync({
        userId: user.id,
        request: {
          amount: adjustForm.amount,
          reason: adjustForm.reason,
          credit_type: adjustForm.credit_type,
        },
      })
      setCreditsOpen(false)
      setAdjustForm({ amount: 0, reason: '', credit_type: 'extra' })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al ajustar creditos'))
    }
  }

  const handleReassignPlan = async () => {
    if (!user || !reassignForm.new_plan_id) return
    try {
      await reassignPlan.mutateAsync({ id: user.id, request: reassignForm })
      setReassignOpen(false)
      setReassignForm({ new_plan_id: '', balance_action: 'absorb', reason: '' })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al reasignar plan'))
    }
  }

  const handleAssignBoost = async () => {
    if (!user || !selectedBoostId) return
    const boost = boosts?.find((b) => b.id === selectedBoostId)
    if (!boost) return
    try {
      await adjustCredits.mutateAsync({
        userId: user.id,
        request: {
          amount: boost.credits,
          reason: `Admin assigned boost: ${boost.name} (${boost.credits} credits)`,
          credit_type: 'extra',
        },
      })
      setBoostOpen(false)
      setSelectedBoostId('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al asignar boost'))
    }
  }

  const handleDeleteUser = async () => {
    if (!user) return
    try {
      await deleteUser.mutateAsync({ id: user.id, request: { confirm_email: deleteEmail } })
      setDeleteOpen(false)
      navigate({ to: '/users' })
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al eliminar usuario'))
    }
  }

  const copyUserCode = () => {
    if (!user?.user_code) return
    navigator.clipboard.writeText(user.user_code)
    setCodeCopied(true)
    setTimeout(() => setCodeCopied(false), 2000)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Usuario no encontrado</p>
        <Button variant="outline" asChild>
          <Link to="/users">Volver a usuarios</Link>
        </Button>
      </div>
    )
  }

  const pct = user.max_docs > 0 ? (user.docs_used / user.max_docs) * 100 : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/users">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Usuarios
          </Link>
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {user.name} {user.lastname}
          </h1>
          <p className="text-muted-foreground">{user.email}</p>
          {user.user_code && (
            <button
              onClick={copyUserCode}
              className="mt-1 inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground hover:bg-muted/80"
            >
              {user.user_code}
              {codeCopied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          )}
          <div className="mt-2 flex gap-2">
            <StatusBadge role={user.role} />
            <StatusBadge status={user.is_suspended ? 'suspended' : 'active'} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Editar
          </Button>
          {isSuperAdmin && user.role !== 'super_admin' && (
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Eliminar
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold capitalize">{user.subscription_level || 'Free'}</p>
            <Button
              variant="link"
              className="h-auto px-0 text-xs"
              onClick={() => setReassignOpen(true)}
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Reasignar plan
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Docs Usados</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{user.docs_used}</p>
            <div className="mt-2">
              <div className="h-2 rounded-full bg-muted">
                <div
                  className={`h-full rounded-full ${pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-yellow-500' : 'bg-primary'}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">de {user.max_docs} max</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Creditos Extra
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{credits ? credits.extra_credits.remaining : 0}</p>
            <p className="text-xs text-muted-foreground">
              {credits
                ? `${credits.extra_credits.used} usados de ${credits.extra_credits.total_purchased}`
                : 'Sin datos'}
            </p>
            <Button
              variant="link"
              className="h-auto px-0 text-xs"
              onClick={() => setBoostOpen(true)}
            >
              <Rocket className="mr-1 h-3 w-3" />
              Asignar boost
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cuota Max</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{user.max_docs}</p>
            <Button variant="link" className="h-auto px-0 text-xs" onClick={openQuota}>
              Ajustar cuota
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="activity">Actividad</TabsTrigger>
          <TabsTrigger value="transactions">Transacciones</TabsTrigger>
          <TabsTrigger value="credits">Creditos</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-muted-foreground">Nacionalidad</dt>
                  <dd className="font-medium">{user.nationality || '-'}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Creado</dt>
                  <dd className="font-medium">
                    {user.created_at ? formatDate(user.created_at) : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Actualizado</dt>
                  <dd className="font-medium">
                    {user.updated_at ? formatDate(user.updated_at) : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Nuevo usuario</dt>
                  <dd className="font-medium">{user.is_new_user ? 'Si' : 'No'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {activity?.dashboards?.length ? (
                <div className="space-y-3">
                  {activity.dashboards.map(
                    (d: {
                      id: string
                      keyword: string
                      created_at: string
                      plan_name: string
                      number_posts: number
                    }) => (
                      <div key={d.id} className="flex items-center gap-3 rounded-lg border p-3">
                        <BarChart3 className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{d.keyword}</p>
                          <p className="text-xs text-muted-foreground">
                            {d.plan_name} - {d.number_posts} posts
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(d.created_at)}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin actividad reciente</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {activity?.transactions?.length ? (
                <div className="space-y-3">
                  {activity.transactions.map(
                    (t: {
                      id: string
                      type: string
                      amount: number
                      status: string
                      created_at: string
                    }) => (
                      <div key={t.id} className="flex items-center gap-3 rounded-lg border p-3">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm font-medium capitalize">{t.type}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(t.amount)}
                          </p>
                        </div>
                        <StatusBadge status={t.status} />
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(t.created_at)}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin transacciones</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credits" className="mt-4">
          {credits ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Creditos del Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {credits.plan_credits.remaining}
                      <span className="text-sm font-normal text-muted-foreground">
                        {' '}
                        / {credits.plan_credits.total}
                      </span>
                    </p>
                    <div className="mt-2">
                      <div className="h-2 rounded-full bg-muted">
                        <div
                          className={`h-full rounded-full ${
                            credits.plan_credits.total > 0 &&
                            credits.plan_credits.used / credits.plan_credits.total >= 0.9
                              ? 'bg-destructive'
                              : credits.plan_credits.total > 0 &&
                                  credits.plan_credits.used / credits.plan_credits.total >= 0.7
                                ? 'bg-yellow-500'
                                : 'bg-primary'
                          }`}
                          style={{
                            width: `${credits.plan_credits.total > 0 ? Math.min((credits.plan_credits.used / credits.plan_credits.total) * 100, 100) : 0}%`,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {credits.plan_credits.used} usados
                      </p>
                    </div>
                    {credits.plan_credits.period_end && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Renueva: {formatDate(credits.plan_credits.period_end)}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Creditos Extra
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {credits.extra_credits.remaining}
                      <span className="text-sm font-normal text-muted-foreground">
                        {' '}
                        / {credits.extra_credits.total_purchased}
                      </span>
                    </p>
                    <div className="mt-2">
                      <div className="h-2 rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-blue-500"
                          style={{
                            width: `${credits.extra_credits.total_purchased > 0 ? Math.min((credits.extra_credits.used / credits.extra_credits.total_purchased) * 100, 100) : 0}%`,
                          }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {credits.extra_credits.used} usados
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Total disponible</p>
                      <p className="flex items-center gap-2 text-3xl font-bold">
                        <Zap className="h-6 w-6 text-yellow-500" />
                        {credits.total_available.toLocaleString()}
                      </p>
                    </div>
                    <Button onClick={() => setCreditsOpen(true)}>Ajustar creditos</Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Este usuario no tiene registro de creditos. Se creara automaticamente al activar
                  el sistema de creditos.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={100}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lastname">Apellido</Label>
              <Input
                id="lastname"
                value={editForm.lastname}
                onChange={(e) => setEditForm((f) => ({ ...f, lastname: e.target.value }))}
                maxLength={100}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                maxLength={254}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="whatsapp">WhatsApp</Label>
              {user.whatsapp_number && !whatsappForm.dirty ? (
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{user.whatsapp_number}</p>
                    <p className="text-xs text-muted-foreground">
                      Consentimiento registrado el{' '}
                      {user.whatsapp_opt_in_at ? formatDateTime(user.whatsapp_opt_in_at) : '-'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setWhatsappForm({ number: '', dirty: true })}
                  >
                    Quitar
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    id="whatsapp"
                    placeholder="09XXXXXXXX o +593XXXXXXXXX"
                    value={whatsappForm.number}
                    onChange={(e) => setWhatsappForm({ number: e.target.value, dirty: true })}
                    maxLength={20}
                  />
                  <p className="text-xs text-muted-foreground">
                    Al guardar se registra el consentimiento del usuario para recibir mensajes.
                  </p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditSave} disabled={updateUser.isPending}>
              {updateUser.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quota Dialog */}
      <Dialog open={quotaOpen} onOpenChange={setQuotaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar cuota de documentos</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="maxDocs">Max documentos</Label>
              <Input
                id="maxDocs"
                type="number"
                min={0}
                value={quotaValue}
                onChange={(e) => setQuotaValue(Number(e.target.value))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuotaOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleQuotaSave} disabled={updateQuota.isPending}>
              {updateQuota.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Credits Dialog */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar creditos</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Cantidad (positivo = agregar, negativo = restar)</Label>
              <Input
                type="number"
                value={adjustForm.amount}
                onChange={(e) => setAdjustForm((f) => ({ ...f, amount: Number(e.target.value) }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Tipo de credito</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="credit_type"
                    checked={adjustForm.credit_type === 'extra'}
                    onChange={() => setAdjustForm((f) => ({ ...f, credit_type: 'extra' }))}
                  />
                  Extra
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="credit_type"
                    checked={adjustForm.credit_type === 'plan'}
                    onChange={() => setAdjustForm((f) => ({ ...f, credit_type: 'plan' }))}
                  />
                  Plan
                </label>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Razon</Label>
              <Input
                placeholder="Razon del ajuste..."
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                maxLength={500}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditsOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAdjustCredits}
              disabled={adjustCredits.isPending || !adjustForm.reason || adjustForm.amount === 0}
            >
              {adjustCredits.isPending ? 'Ajustando...' : 'Ajustar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Plan Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reasignar Plan</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Plan actual</Label>
              <p className="text-sm font-medium capitalize">{user.subscription_level || 'Free'}</p>
            </div>
            <div className="grid gap-2">
              <Label>Nuevo plan</Label>
              <Select
                value={reassignForm.new_plan_id}
                onValueChange={(v) => setReassignForm((f) => ({ ...f, new_plan_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans
                    ?.filter((p) => p.plan_id !== user.subscription_level)
                    .map((plan) => (
                      <SelectItem key={plan.id} value={plan.plan_id}>
                        {plan.display_name || plan.name} -{' '}
                        {(plan.credits_per_month ?? 0).toLocaleString()} creditos/mes
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Accion con creditos restantes</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="balance_action"
                    checked={reassignForm.balance_action === 'credit'}
                    onChange={() => setReassignForm((f) => ({ ...f, balance_action: 'credit' }))}
                  />
                  <span>
                    <strong>Abonar</strong> - Agregar restantes como creditos extra
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="balance_action"
                    checked={reassignForm.balance_action === 'absorb'}
                    onChange={() => setReassignForm((f) => ({ ...f, balance_action: 'absorb' }))}
                  />
                  <span>
                    <strong>Absorber</strong> - Comenzar fresco con nuevo plan
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="balance_action"
                    checked={reassignForm.balance_action === 'debit'}
                    onChange={() => setReassignForm((f) => ({ ...f, balance_action: 'debit' }))}
                  />
                  <span>
                    <strong>Restar</strong> - Descontar restantes del nuevo plan
                  </span>
                </label>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Razon (opcional)</Label>
              <Input
                placeholder="Razon del cambio..."
                value={reassignForm.reason}
                onChange={(e) => setReassignForm((f) => ({ ...f, reason: e.target.value }))}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleReassignPlan}
              disabled={reassignPlan.isPending || !reassignForm.new_plan_id}
            >
              {reassignPlan.isPending ? 'Reasignando...' : 'Reasignar Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Boost Dialog */}
      <Dialog open={boostOpen} onOpenChange={setBoostOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Boost de Creditos</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Boost</Label>
              <Select value={selectedBoostId} onValueChange={setSelectedBoostId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar boost" />
                </SelectTrigger>
                <SelectContent>
                  {boosts
                    ?.filter((b) => b.is_active)
                    .map((boost) => (
                      <SelectItem key={boost.id} value={boost.id}>
                        {boost.name} - {boost.credits.toLocaleString()} creditos
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {selectedBoostId && boosts && (
              <div className="rounded-lg border bg-muted/50 p-3">
                {(() => {
                  const boost = boosts.find((b) => b.id === selectedBoostId)
                  if (!boost) return null
                  return (
                    <div className="text-sm">
                      <p className="font-medium">{boost.name}</p>
                      <p className="text-muted-foreground">
                        {boost.credits.toLocaleString()} creditos extra
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Precio normal: ${boost.price_usd} (se asigna gratis)
                      </p>
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBoostOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAssignBoost}
              disabled={adjustCredits.isPending || !selectedBoostId}
            >
              {adjustCredits.isPending ? 'Asignando...' : 'Asignar Boost'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Eliminar usuario permanentemente</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Esta accion es <strong>irreversible</strong>. Se eliminaran todos los datos del
              usuario incluyendo dashboards, posts y creditos. Las transacciones se conservan como
              registros financieros.
            </p>
            <div className="grid gap-2">
              <Label>
                Escribe el email del usuario para confirmar: <strong>{user.email}</strong>
              </Label>
              <Input
                placeholder={user.email}
                value={deleteEmail}
                onChange={(e) => setDeleteEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteOpen(false)
                setDeleteEmail('')
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteUser.isPending || deleteEmail !== user.email}
            >
              {deleteUser.isPending ? 'Eliminando...' : 'Eliminar permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
